/**
 * Shared types for the smart framing / face detection pipeline.
 *
 * Centralised here so both the service layer and the hook layer
 * (useFaceDetections) can depend on a single source of truth.
 */

// ---------------------------------------------------------------------------
// Face Detection
// ---------------------------------------------------------------------------

/**
 * A single face detection result at a specific media time.
 * `box` is normalised [x, y, width, height] in 0‒1 space.
 */
export type FaceDetection = {
  time: number;
  id: number;
  label: string;
  box: [number, number, number, number];
  confidence: number;
};

/** Label → array of detection IDs (face tracks). */
export type FaceTracks = Record<string, FaceDetection['id'][]>;

/** Full response from the face-detections API endpoint. */
export type FaceDetectionsResponse = {
  detections: FaceDetection[];
  faceTracks: FaceTracks;
  speakerMapping: FaceToSpeakerLabelMapping | null;
  speakerIdentifications: SpeakerIdentifications | null;
};

// ---------------------------------------------------------------------------
// Speaker Intervals
// ---------------------------------------------------------------------------

/**
 * A continuous interval where a single speaker is speaking.
 * Times are in the same coordinate space as the transcript (media time).
 */
export type SpeakerInterval = {
  speakerLabel: string;
  startTime: number;
  endTime: number;
};

/**
 * Face-label → speaker-label mapping returned by the face-detections API.
 * e.g. `{ "face_0": "spk_0", "face_1": "spk_1" }`
 */
export type FaceToSpeakerLabelMapping = Record<string, string>;

/**
 * Face-label → speakerId mapping returned by the face-detections API
 * via `speaker_identifications`.
 * e.g. `{ "face_0": "dato-speaker-id-abc", "face_1": "dato-speaker-id-xyz" }`
 */
export type SpeakerIdentifications = Record<string, string>;

/** Speaker-label → array of face detections belonging to that active speaker. */
export type ActiveSpeakerFaceDetections = Record<string, FaceDetection[]>;

// ---------------------------------------------------------------------------
// Scene Framing Intervals (new – PLAT-6535)
// ---------------------------------------------------------------------------

/** Discriminant for the kind of framing interval. */
export type SceneFramingIntervalType =
  | 'active_speaker'
  | 'non_active_speaker'
  | 'no_speaker';

/**
 * A time interval within a scene together with its dominant face detections.
 *
 * - `active_speaker`      – the face belongs to the person who is speaking.
 * - `non_active_speaker`  – a speaker is talking but their face was not detected;
 *                           the face is inferred from a neighbour or most-frequent fallback.
 * - `no_speaker`          – silence gap ≥ threshold; face chosen the same way.
 *
 * Times are *relative to the scene start* (0 = scene start).
 */
export type SceneFramingInterval = {
  startRelativeTime: number;
  endRelativeTime: number;
  type: SceneFramingIntervalType;
  faceDetections: FaceDetection[];
};

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/** Tuneable knobs for `buildSceneFramingIntervals`. */
export type SceneFramingConfig = {
  /**
   * Minimum silence gap (seconds) to treat as a `no_speaker` interval.
   * Gaps shorter than this are absorbed into the preceding speaker interval.
   * @default 1
   */
  silenceThresholdSec: number;
};

export const DEFAULT_SCENE_FRAMING_CONFIG: SceneFramingConfig = {
  silenceThresholdSec: 1,
};

// ---------------------------------------------------------------------------
// Face Mapping Admin
// ---------------------------------------------------------------------------

/** A face label paired with the number of detections that share it. */
export type FaceFrequency = {
  faceLabel: string;
  count: number;
};

/** Response from `POST /api/story/{id}/identify-speakers`. */
export type IdentifySpeakersResponse = {
  mode: 'identify-only' | 'full-detection';
  message: string;
  invocationId?: string;
  executionArn?: string;
  archivedFaceTracks?: string;
};
